import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../api";
import type { User } from "../types";
import { Card, ErrorNotice, Loading, PageHeader } from "./Ui";
import { formatDate, label } from "../utils";
import { usePermissions } from "../permissions";
import { DeliveryOverview, type DeliverySummary } from "./DeliveryOverview";
import { NativeCapacity } from "./NativeCapacity";

type Plan = {
  department: string;
  period: string;
  unit: "hours" | "points";
  available: number;
  atCapacityPercent: number;
};
type Source = {
  id: string;
  provider: string;
  department: string;
  period: string;
  unit: "hours" | "points";
  scope: string;
  credentialRef: string;
  enabled: boolean;
  linear?: {
    cycleMode: "active";
    capacityPolicy?: {
      excludedAssignees: string[];
      projects: { projectId: string; category: string }[];
    };
    historyCycles: number;
    rules: {
      smallPoints: number;
      smallTaskDays: number;
      staleDays: number;
      reviewDays: number;
      carryOverCycles: number;
      reviewStatuses: string[];
      blockedStatuses: string[];
    };
  };
  mapping: { status: string; estimate: string; activeStatuses: string[] };
};
type Config = { version: number; plans: Plan[]; sources: Source[] };
type Provider = {
  id: string;
  label: string;
  unit: string;
  estimateBasis: string;
};
export type Overview = {
  items: (Omit<Plan, "available"> & {
    available: number | null;
    capacityBasis: string;
    committedEffort: number;
    effort: number;
    count: number;
    status: string;
    utilisation: number | null;
    issues: string[];
  })[];
  sources: {
    id: string;
    provider: string;
    department: string;
    period: string;
    enabled: boolean;
    syncedAt: string | null;
    error: string | null;
    delivery: DeliverySummary | null;
  }[];
};
export function CapacityPage({ user: _user }: { user: User }) {
  const api = useApi();
  const permissions = usePermissions();
  const client = useQueryClient();
  const overview = useQuery({
    queryKey: ["capacity"],
    queryFn: () => api.get<Overview>("/capacity/"),
    refetchInterval: 60000,
    enabled: permissions.can("capacity.view"),
  });
  const config = useQuery({
    queryKey: ["capacity-config"],
    queryFn: () =>
      api.get<{ config: Config; providers: Provider[] }>("/capacity/config"),
    enabled: permissions.can("integrations.manage"),
  });
  const sync = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; error: string | null }>(
        `/capacity/sources/${id}/sync`,
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["capacity"] });
      await client.invalidateQueries({ queryKey: ["delivery-details"] });
    },
  });
  return (
    <div className="page scroll-page capacity-page">
      <PageHeader
        title="Capacity health"
        description="Plan approved initiatives against your departments’ available delivery time."
      />
      <div className="content-grid">
        {permissions.nativeCapacity && <NativeCapacity />}
        {permissions.can("capacity.view") && (
          <Card className="span-2">
            <h2>Delivery capacity</h2>
            <p className="section-help">
              Fixed plans compare outstanding effort with configured
              availability. Automatic Linear plans compare eligible unfinished
              delivery effort (excluding Todo, Backlog and configured people)
              with median throughput from at least three completed cycles of the
              same duration. This is a planning signal, not measured spare
              capacity or an individual productivity score. Leave and
              operational commitments are not inferred. Missing estimates or
              stale imports prevent a healthy status. Never compare points
              across teams.
            </p>
            {overview.isPending ? (
              <Loading />
            ) : overview.isError ? (
              <ErrorNotice error={overview.error} />
            ) : overview.data.items.length ? (
              <div className="table-wrap">
                <table className="initiative-table">
                  <thead>
                    <tr>
                      {[
                        "Department / period",
                        "Workload",
                        "Availability / baseline",
                        "Load ratio",
                        "Health",
                      ].map((h) => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {overview.data.items.map((p) => (
                      <tr key={`${p.department}-${p.period}`}>
                        <td>
                          {p.department}
                          <br />
                          {p.period}
                        </td>
                        <td>
                          {p.effort} {p.unit} · {p.count} items
                          {p.capacityBasis === "historical_throughput" && (
                            <p className="muted">
                              Completed work is excluded from outstanding
                              effort.
                            </p>
                          )}
                        </td>
                        <td>
                          {p.available ?? "Unknown"}{" "}
                          {p.available !== null && p.unit}
                          <p className="muted">{label(p.capacityBasis)}</p>
                        </td>
                        <td>
                          {p.utilisation === null
                            ? "Unknown"
                            : `${p.utilisation}%`}
                        </td>
                        <td>
                          {p.capacityBasis === "historical_throughput" &&
                          p.status !== "insufficient_data"
                            ? p.status === "over_capacity"
                              ? "Above historical throughput"
                              : p.status === "at_capacity"
                                ? "Near historical throughput"
                                : "Below historical throughput"
                            : label(p.status)}
                          {p.issues.map((i) => (
                            <p className="muted" key={i}>
                              {i}
                            </p>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>
                No capacity plans configured. An integration manager can add a
                department and connect its delivery scope below.
              </p>
            )}
          </Card>
        )}
        {permissions.can("capacity.view") && (
          <Card className="span-2">
            <h2>Source health</h2>
            <p className="section-help">
              Imports are read-only. No changes are sent to external tools. Use
              a cycle, sprint list or dedicated Notion data source covering
              exactly the plan period. Do not connect mirrored copies of the
              same work.
            </p>
            {overview.data?.sources.map((s) => (
              <div className="metric-row" key={s.id}>
                <div>
                  <strong>
                    {s.id} · {label(s.provider)}
                  </strong>
                  <p>
                    {s.department} / {s.period} ·{" "}
                    {s.enabled ? "Enabled" : "Disabled"} · Last attempt:{" "}
                    {formatDate(s.syncedAt)}
                  </p>
                  {s.error && <p role="alert">{s.error}</p>}
                </div>
                {permissions.can("integrations.manage") && (
                  <button
                    className="btn-outline"
                    disabled={!s.enabled || sync.isPending}
                    onClick={() => sync.mutate(s.id)}
                  >
                    {sync.isPending && sync.variables === s.id
                      ? "Syncing…"
                      : "Sync now"}
                  </button>
                )}
              </div>
            ))}
            {sync.isError && <ErrorNotice error={sync.error} />}
            {sync.isSuccess && (
              <p role="status">
                {sync.data.ok ? "Sync completed." : sync.data.error}
              </p>
            )}
          </Card>
        )}
        {permissions.can("capacity.view") &&
          overview.data?.sources
            .filter((s) => s.delivery)
            .map((s) => (
              <DeliveryOverview
                key={s.id}
                sourceId={s.id}
                summary={s.delivery!}
                syncedAt={s.syncedAt}
              />
            ))}
        {permissions.can("integrations.manage") && (
          <Card className="span-2">
            <h2>Configuration</h2>
            <p className="section-help">
              Only secret references are stored here. Provision the named
              environment variable from Azure Key Vault; never paste tokens into
              these fields. Saving invalidates earlier snapshots until the next
              sync.
            </p>
            {config.isPending ? (
              <Loading />
            ) : config.isError ? (
              <ErrorNotice error={config.error} />
            ) : (
              <CapacityForm
                key={config.data.config.version}
                initial={config.data.config}
                providers={config.data.providers}
              />
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
function CapacityForm({
  initial,
  providers,
}: {
  initial: Config;
  providers: Provider[];
}) {
  const api = useApi();
  const client = useQueryClient();
  const [config, setConfig] = useState(initial);
  const projects = useQuery({
    queryKey: ["capacity"],
    queryFn: () => api.get<Overview>("/capacity/"),
  });
  const [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: () =>
      api.put("/capacity/config", { config, changeReason: reason }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["capacity-config"] });
      await client.invalidateQueries({ queryKey: ["capacity"] });
    },
  });
  const plan = (i: number, patch: Partial<Plan>) =>
    setConfig((c) => ({
      ...c,
      plans: c.plans.map((p, n) => (n === i ? { ...p, ...patch } : p)),
    }));
  const source = (i: number, patch: Partial<Source>) =>
    setConfig((c) => ({
      ...c,
      sources: c.sources.map((s, n) => (n === i ? { ...s, ...patch } : s)),
    }));
  return (
    <form
      className="portfolio-form"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h3>Department plans</h3>
      <p className="section-help">
        Automatic Linear connections use historical throughput, so configured
        availability is ignored for their plan.
      </p>
      {config.plans.map((p, i) => (
        <fieldset key={i} className="capacity-fields">
          <legend>Plan {i + 1}</legend>
          <label>
            Department
            <input
              required
              minLength={2}
              maxLength={100}
              value={p.department}
              onChange={(e) => plan(i, { department: e.target.value })}
            />
          </label>
          <label>
            Planning period
            <input
              required
              minLength={2}
              maxLength={80}
              placeholder="2026 sprint 20"
              value={p.period}
              onChange={(e) => plan(i, { period: e.target.value })}
            />
          </label>
          <label>
            Effort unit
            <select
              value={p.unit}
              onChange={(e) =>
                plan(i, { unit: e.target.value as Plan["unit"] })
              }
            >
              <option value="hours">Hours</option>
              <option value="points">Team story points</option>
            </select>
          </label>
          {config.sources.some(
            (s) =>
              s.linear &&
              s.department === p.department &&
              s.period === p.period,
          ) ? (
            <p className="section-help">
              Baseline: automatic median of comparable completed Linear cycles.
              No manual availability value is used.
            </p>
          ) : (
            <label>
              Available effort in period
              <input
                type="number"
                required
                min={0.01}
                max={1000000}
                step="any"
                value={p.available}
                onChange={(e) => plan(i, { available: Number(e.target.value) })}
              />
            </label>
          )}
          <label>
            At-capacity threshold (%)
            <input
              type="number"
              required
              min={1}
              max={100}
              value={p.atCapacityPercent}
              onChange={(e) =>
                plan(i, { atCapacityPercent: Number(e.target.value) })
              }
            />
          </label>
          <button
            className="btn-outline"
            type="button"
            onClick={() =>
              setConfig((c) => ({
                ...c,
                plans: c.plans.filter((_, n) => n !== i),
              }))
            }
          >
            Remove plan {i + 1}
          </button>
        </fieldset>
      ))}
      <button
        className="btn-outline"
        type="button"
        onClick={() =>
          setConfig((c) => ({
            ...c,
            plans: [
              ...c.plans,
              {
                department: "",
                period: "",
                unit: "hours",
                available: 1,
                atCapacityPercent: 90,
              },
            ],
          }))
        }
      >
        Add department plan
      </button>
      <h3>Tool connections</h3>
      {config.sources.map((s, i) => (
        <fieldset key={i} className="capacity-fields">
          <legend>Connection {i + 1}</legend>
          <label>
            Connection ID
            <input
              required
              pattern="[a-z0-9][a-z0-9_-]{1,63}"
              value={s.id}
              onChange={(e) => source(i, { id: e.target.value })}
            />
          </label>
          <label>
            Tool
            <select
              value={s.provider}
              onChange={(e) => {
                const p = providers.find((p) => p.id === e.target.value);
                source(i, {
                  provider: e.target.value,
                  unit: p?.unit === "points" ? "points" : "hours",
                  linear: undefined,
                });
              }}
            >
              {providers.map((p) => (
                <option value={p.id} key={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Department
            <input
              required
              value={s.department}
              onChange={(e) => source(i, { department: e.target.value })}
            />
          </label>
          <label>
            Planning period
            <input
              required
              value={s.period}
              onChange={(e) => source(i, { period: e.target.value })}
            />
          </label>
          <label>
            {s.linear ? "Linear team key" : "Cycle / list / data source ID"}
            <input
              required
              pattern="[a-zA-Z0-9_-]{1,100}"
              value={s.scope}
              onChange={(e) => source(i, { scope: e.target.value })}
            />
          </label>
          {s.provider === "linear" && (
            <>
              <label>
                Cycle selection
                <select
                  value={s.linear ? "active" : "fixed"}
                  onChange={(e) =>
                    source(i, {
                      linear:
                        e.target.value === "active"
                          ? {
                              cycleMode: "active",
                              capacityPolicy: {
                                excludedAssignees: [
                                  "syd.reynolds",
                                  "tapas.sarkar",
                                  "james.amo",
                                ],
                                projects: [],
                              },
                              historyCycles: 6,
                              rules: {
                                smallPoints: 3,
                                smallTaskDays: 3,
                                staleDays: 5,
                                reviewDays: 2,
                                carryOverCycles: 2,
                                reviewStatuses: [
                                  "In Review",
                                  "Ready for Review",
                                  "Ready for QA",
                                ],
                                blockedStatuses: ["Blocked"],
                              },
                            }
                          : undefined,
                    })
                  }
                >
                  <option value="fixed">Fixed cycle ID</option>
                  <option value="active">
                    Automatically detect active cycle by team key
                  </option>
                </select>
              </label>
              {s.linear && (
                <>
                  <label>
                    Excluded assignees (email, email prefix or Linear user ID;
                    comma-separated)
                    <input
                      value={(
                        s.linear.capacityPolicy?.excludedAssignees ?? []
                      ).join(",")}
                      onChange={(e) =>
                        source(i, {
                          linear: {
                            ...s.linear!,
                            capacityPolicy: {
                              projects:
                                s.linear!.capacityPolicy?.projects ?? [],
                              excludedAssignees: e.target.value.split(","),
                            },
                          },
                        })
                      }
                      onBlur={() =>
                        source(i, {
                          linear: {
                            ...s.linear!,
                            capacityPolicy: {
                              projects:
                                s.linear!.capacityPolicy?.projects ?? [],
                              excludedAssignees: (
                                s.linear!.capacityPolicy?.excludedAssignees ??
                                []
                              )
                                .map((v) => v.trim())
                                .filter(Boolean),
                            },
                          },
                        })
                      }
                    />
                  </label>
                  <div className="span-2">
                    <h4>Work categories by Linear project</h4>
                    <p className="section-help">
                      Manual mapping by project ID. Unmapped work stays
                      Unclassified; classifications apply to imported history
                      too. Mixed projects should stay Unclassified until
                      separated. No Linear issues are changed.
                    </p>
                    {(
                      projects.data?.sources.find((v) => v.id === s.id)
                        ?.delivery?.projects ?? []
                    )
                      .filter((p) => p.id !== "none")
                      .map((p) => (
                        <label key={p.id}>
                          {p.name}
                          <select
                            value={
                              s.linear!.capacityPolicy?.projects.find(
                                (v) => v.projectId === p.id,
                              )?.category ?? "unclassified"
                            }
                            onChange={(e) =>
                              source(i, {
                                linear: {
                                  ...s.linear!,
                                  capacityPolicy: {
                                    excludedAssignees:
                                      s.linear!.capacityPolicy
                                        ?.excludedAssignees ?? [],
                                    projects: [
                                      ...(
                                        s.linear!.capacityPolicy?.projects ?? []
                                      ).filter((v) => v.projectId !== p.id),
                                      {
                                        projectId: p.id,
                                        category: e.target.value,
                                      },
                                    ],
                                  },
                                },
                              })
                            }
                          >
                            <option value="unclassified">Unclassified</option>
                            <option value="run">
                              Run — recurring operations
                            </option>
                            <option value="build">
                              Build — planned change
                            </option>
                            <option value="unplanned">Unplanned work</option>
                          </select>
                        </label>
                      ))}
                    {!projects.data?.sources.find((v) => v.id === s.id)
                      ?.delivery && (
                      <p>Sync this connection to choose projects.</p>
                    )}
                  </div>
                  <label>
                    Completed cycles to import
                    <input
                      type="number"
                      min={3}
                      max={12}
                      required
                      value={s.linear.historyCycles}
                      onChange={(e) =>
                        source(i, {
                          linear: {
                            ...s.linear!,
                            historyCycles: Number(e.target.value),
                          },
                        })
                      }
                    />
                  </label>
                  {(
                    [
                      ["smallPoints", "Small-task maximum points", 0, 100],
                      [
                        "smallTaskDays",
                        "Small-task ageing (working days)",
                        1,
                        90,
                      ],
                      [
                        "staleDays",
                        "No-status-movement warning (working days)",
                        1,
                        90,
                      ],
                      ["reviewDays", "Review waiting (working days)", 1, 90],
                      ["carryOverCycles", "Carry-over warning (cycles)", 1, 12],
                    ] as const
                  ).map(([key, title, min, max]) => (
                    <label key={key}>
                      {title}
                      <input
                        required
                        type="number"
                        min={min}
                        max={max}
                        value={s.linear!.rules[key]}
                        onChange={(e) =>
                          source(i, {
                            linear: {
                              ...s.linear!,
                              rules: {
                                ...s.linear!.rules,
                                [key]: Number(e.target.value),
                              },
                            },
                          })
                        }
                      />
                    </label>
                  ))}
                  {(
                    [
                      ["reviewStatuses", "Review statuses"],
                      ["blockedStatuses", "Blocked statuses"],
                    ] as const
                  ).map(([key, title]) => (
                    <label key={key}>
                      {title}
                      <input
                        value={s.linear!.rules[key].join(",")}
                        onChange={(e) =>
                          source(i, {
                            linear: {
                              ...s.linear!,
                              rules: {
                                ...s.linear!.rules,
                                [key]: e.target.value.split(","),
                              },
                            },
                          })
                        }
                        onBlur={() =>
                          source(i, {
                            linear: {
                              ...s.linear!,
                              rules: {
                                ...s.linear!.rules,
                                [key]: s
                                  .linear!.rules[key].map((v) => v.trim())
                                  .filter(Boolean),
                              },
                            },
                          })
                        }
                      />
                    </label>
                  ))}
                  <p className="section-help">
                    Uses the active cycle, completed-cycle history, and
                    unfinished delivery work outside the current cycle. Status
                    names must match Linear exactly. Weekends are excluded;
                    public holidays are not. Saving rules requires a fresh sync.
                  </p>
                </>
              )}
            </>
          )}
          <label>
            Effort unit
            <select
              value={s.unit}
              onChange={(e) =>
                source(i, { unit: e.target.value as Source["unit"] })
              }
            >
              <option value="hours">Hours</option>
              <option value="points">Team story points</option>
            </select>
          </label>
          <label>
            Secret environment reference
            <input
              required
              pattern="CAPACITY_TOKEN_[A-Z0-9_]{1,80}"
              placeholder="CAPACITY_TOKEN_ENGINEERING"
              value={s.credentialRef}
              onChange={(e) => source(i, { credentialRef: e.target.value })}
            />
          </label>
          <label>
            Included delivery statuses (comma-separated)
            <input
              required
              value={s.mapping.activeStatuses.join(",")}
              onChange={(e) =>
                source(i, {
                  mapping: {
                    ...s.mapping,
                    activeStatuses: e.target.value.split(","),
                  },
                })
              }
              onBlur={() =>
                source(i, {
                  mapping: {
                    ...s.mapping,
                    activeStatuses: s.mapping.activeStatuses
                      .map((v) => v.trim())
                      .filter(Boolean),
                  },
                })
              }
            />
          </label>
          {s.provider === "notion" && (
            <>
              <label>
                Status property
                <input
                  required
                  value={s.mapping.status}
                  onChange={(e) =>
                    source(i, {
                      mapping: { ...s.mapping, status: e.target.value },
                    })
                  }
                />
              </label>
              <label>
                Numeric effort property
                <input
                  required
                  value={s.mapping.estimate}
                  onChange={(e) =>
                    source(i, {
                      mapping: { ...s.mapping, estimate: e.target.value },
                    })
                  }
                />
              </label>
            </>
          )}
          <label>
            Sync
            <select
              value={String(s.enabled)}
              onChange={(e) =>
                source(i, { enabled: e.target.value === "true" })
              }
            >
              <option value="false">Disabled</option>
              <option value="true">Enabled</option>
            </select>
          </label>
          <p className="section-help">
            {providers.find((p) => p.id === s.provider)?.estimateBasis}
          </p>
          <button
            className="btn-outline"
            type="button"
            onClick={() =>
              setConfig((c) => ({
                ...c,
                sources: c.sources.filter((_, n) => n !== i),
              }))
            }
          >
            Remove connection {i + 1}
          </button>
        </fieldset>
      ))}
      <button
        className="btn-outline"
        type="button"
        onClick={() =>
          setConfig((c) => ({
            ...c,
            sources: [
              ...c.sources,
              {
                id: "",
                provider: "linear",
                department: "",
                period: "",
                unit: "points",
                scope: "",
                credentialRef: "CAPACITY_TOKEN_",
                enabled: false,
                mapping: {
                  status: "Status",
                  estimate: "Remaining effort",
                  activeStatuses: ["In Progress"],
                },
              },
            ],
          }))
        }
      >
        Add tool connection
      </button>
      <label>
        Change reason
        <input
          required
          minLength={3}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <button className="btn-primary" disabled={save.isPending}>
        {save.isPending ? "Saving…" : "Save configuration"}
      </button>
      {save.isError && <ErrorNotice error={save.error} />}
    </form>
  );
}
